import { uniqueId } from "lodash";
import * as React from "react";
import { FlyoutLink } from "@app/shared/Flyouts";
import { GapFillStatusFlyout } from "@app/projects/network-assessment/GapFillStatusFlyout";
import { GapFillStatusHistoryFlyout } from "@app/projects/network-assessment/GapFillStatusHistoryFlyout";
import { ResultRegion } from "@app/projects/results/common";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import GapFillStatusRecord from "@app/projects/network-assessment/gapFillStatusRecord";
import { GapFillStatusDescriptionMap } from "@app/projects/network-assessment/adequacy/gapFillStatusDescriptionMap";

interface GapFillStatusEditorProps {
  clearCacheImpactAnalysisRunConfigurations: () => void;
  customProjectId: number;
  projectRunConfigurationId: number;
  record: GapFillStatusRecord;
  gapFillDescriptionMap: GapFillStatusDescriptionMap;
  exception?: SpecialtyGroupException | null;
  exceptionIsEditable?: boolean;
  region?: ResultRegion;
  specialtyGroupName: string;
  specialtyGroupCode: string;
  onSubmitException?: (exception: SpecialtyGroupException) => void;
}

const GapFillStatusEditor: React.FC<React.PropsWithChildren<GapFillStatusEditorProps>> = (props: GapFillStatusEditorProps) => {
  const statusId = uniqueId("gap-fill-status-editor-status-");

  const user = React.useContext(UserContext);

  const showHistory = (): boolean => {
    if (user.roles.includes(Roles.GAP_RESOLUTION_STATUS_MANAGER)) {
      return !!props.record.metCriteria && !!props.record.gapFillStatusUpdatedDate;
    } else if (!!props.record.gapFillStatusUpdatedDate) {
      return true;
    }

    return false;
  }

  return (
    <>
      <div className="gap-fill-status-editor-trigger">
        {!props.record.metCriteria &&
          <RoleRestricted allowedRoles={[Roles.GAP_RESOLUTION_STATUS_MANAGER]}>
            <FlyoutLink
              classNames="qes-button-secondary"
              content={{
                body: <GapFillStatusFlyout
                  customProjectId={props.customProjectId}
                  projectRunConfigurationId={props.projectRunConfigurationId}
                  record={props.record}
                  statusId={statusId}
                  comment={props.record.gapFillStatusComment}
                  clearCacheImpactAnalysisRunConfigurations={props.clearCacheImpactAnalysisRunConfigurations}
                  exception={props.exception}
                  exceptionIsEditable={props.exceptionIsEditable}
                  gapFillDescriptionMap={props.gapFillDescriptionMap}
                  region={props.region}
                  specialtyGroupName={props.specialtyGroupName}
                  specialtyGroupCode={props.specialtyGroupCode}
                  onSubmitException={props.onSubmitException}
                />,
                title: "Gap Resolution Status",
              }}
              size="40"
              testId="gap-fill-status-editor__view-update-status-flyout"
            >
              Manage Gap Resolution Status
            </FlyoutLink>
          </RoleRestricted>
        }
        {showHistory() &&
          <FlyoutLink
            classNames="qes-button-secondary"
            content={{
              body: <GapFillStatusHistoryFlyout
                customProjectId={props.customProjectId}
                projectRunConfigurationId={props.projectRunConfigurationId}
                regionSetId={props.record.regionSetId}
                specialtyGroupCode={props.record.specialtyGroupCode}
                clearCacheImpactAnalysisRunConfigurations={props.clearCacheImpactAnalysisRunConfigurations}
                gapFillDescriptionMap={props.gapFillDescriptionMap}
              />,
              title: "Gap Resolution History",
            }}
            size="40"
            testId="gap-fill-status-editor__view-update-status-flyout"
          >
            View Gap Resolution History
          </FlyoutLink>
        }
      </div>
    </>
  );
}

export default GapFillStatusEditor;
